/**
 * Schema helpers for the PasswordRuleSettings baseline.
 *
 * The baseline maps to a Microsoft Graph directorySetting (`GET /settings`) whose
 * `values` array holds name/value string pairs. The JSON schema constrains that array
 * with one `allOf`/`contains` entry per required value.
 */

interface ContainsValue {
    contains: {
        type: 'object';
        properties: {
            name: { type: 'string'; const: string };
            value: { type: 'string'; const: string };
        };
        required: ['name', 'value'];
    };
}

export interface PasswordRuleSettingsSchema {
    type: 'object';
    properties: {
        values: {
            type: 'array';
            allOf: ContainsValue[];
        };
    };
    required: ['values'];
}

export interface PasswordRuleSettingsFormValue {
    BannedPasswordCheckOnPremisesMode: string;
    EnableBannedPasswordCheckOnPremises: boolean;
    EnableBannedPasswordCheck: boolean;
    LockoutDurationInSeconds: number;
    LockoutThreshold: number;
    BannedPasswordList: string;
}

/** Read the desired `const` for a named value out of an existing schema. */
export function extractPasswordRuleSettingsValue(
    schema: PasswordRuleSettingsSchema | undefined,
    name: string,
): string | undefined {
    const item = schema?.properties?.values?.allOf?.find((v) => v?.contains?.properties?.name?.const === name);
    return item?.contains?.properties?.value?.const;
}

function toContains(name: string, value: string): ContainsValue {
    return {
        contains: {
            type: 'object',
            properties: {
                name: { type: 'string', const: name },
                value: { type: 'string', const: value },
            },
            required: ['name', 'value'],
        },
    };
}

/** Build a PasswordRuleSettings schema from the desired-value form. */
export function generatePasswordRuleSettingsSchema(
    formValue: PasswordRuleSettingsFormValue,
): PasswordRuleSettingsSchema {
    const values: Array<{ name: string; value: string }> = [
        { name: 'BannedPasswordCheckOnPremisesMode', value: formValue.BannedPasswordCheckOnPremisesMode },
        {
            name: 'EnableBannedPasswordCheckOnPremises',
            value: formValue.EnableBannedPasswordCheckOnPremises ? 'True' : 'False',
        },
        { name: 'EnableBannedPasswordCheck', value: formValue.EnableBannedPasswordCheck ? 'True' : 'False' },
        { name: 'LockoutDurationInSeconds', value: String(formValue.LockoutDurationInSeconds) },
        { name: 'LockoutThreshold', value: String(formValue.LockoutThreshold) },
        {
            name: 'BannedPasswordList',
            // Only meaningful when the custom banned-password list is being enforced.
            // The list is stored newline-separated in the form but Graph expects it tab-separated.
            value: formValue.EnableBannedPasswordCheck
                ? (formValue.BannedPasswordList ?? '').replaceAll('\n', '\t')
                : '',
        },
    ];

    // The on-premises protection mode is only relevant when on-premises protection is enabled —
    // omit it from the schema entirely when disabled so it isn't checked for deviation.
    const filteredValues = values.filter(
        (v) => !(v.name === 'BannedPasswordCheckOnPremisesMode' && !formValue.EnableBannedPasswordCheckOnPremises),
    );

    return {
        type: 'object',
        properties: {
            values: {
                type: 'array',
                allOf: filteredValues.map((v) => toContains(v.name, v.value)),
            },
        },
        required: ['values'],
    };
}
